const COOKIE_NAME = "gohub_utm";
const COOKIE_DAYS = 90;

export interface UtmData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  landing_page?: string;
  referrer?: string;
}

const UTM_KEYS: (keyof UtmData)[] = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
];

const CONSENT_KEY = "gohub_cookie_consent";

/** In-memory attribution for the current pageview / SPA session (no storage). */
let memoryUtm: UtmData | null = null;

function setCookie(name: string, value: string, days: number) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax;Secure`;
}

function deleteCookie(name: string) {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax;Secure`;
}

function getCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : undefined;
}

function hasMarketingConsent(): boolean {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return false;
    return (JSON.parse(raw) as { marketing?: boolean }).marketing === true;
  } catch {
    return false;
  }
}

/**
 * Read UTM attribution. Prefers the persisted cookie, falls back to the
 * in-memory capture for the current (non-consented) session.
 */
export function getUtmData(): UtmData {
  const raw = getCookie(COOKIE_NAME);
  if (raw) {
    try {
      return JSON.parse(raw) as UtmData;
    } catch {
      /* fall through to memory */
    }
  }
  return memoryUtm ?? {};
}

/**
 * Write or remove the attribution cookie based on the visitor's consent.
 * Called from the cookie banner whenever consent changes.
 */
export function syncUtmCookieWithConsent(prefs: { marketing?: boolean }) {
  if (typeof document === "undefined") return;
  if (prefs?.marketing) {
    const data = getUtmData();
    if (Object.keys(data).length > 0) {
      setCookie(COOKIE_NAME, JSON.stringify(data), COOKIE_DAYS);
    }
  } else {
    deleteCookie(COOKIE_NAME);
  }
}

/**
 * Should be called once on app init.
 * Always captures attribution in memory; only persists to the cookie when
 * the visitor has given marketing consent.
 */
export function captureUtmParams() {
  const params = new URLSearchParams(window.location.search);

  const utmFromUrl: UtmData = {};
  let hasUtm = false;

  for (const key of UTM_KEYS) {
    const val = params.get(key);
    if (val) {
      utmFromUrl[key] = val;
      hasUtm = true;
    }
  }

  const existing = getCookie(COOKIE_NAME);

  if (hasUtm) {
    utmFromUrl.landing_page = window.location.href;
    utmFromUrl.referrer = document.referrer || "";
    memoryUtm = utmFromUrl;
  } else if (!existing && !memoryUtm) {
    memoryUtm = {
      landing_page: window.location.href,
      referrer: document.referrer || "",
    };
  }

  if (hasMarketingConsent()) {
    if (hasUtm || !existing) {
      const data = getUtmData();
      if (Object.keys(data).length > 0) {
        setCookie(COOKIE_NAME, JSON.stringify(data), COOKIE_DAYS);
      }
    }
  }
}
